/**
 * Currency Converter Service
 * Handles currency conversion for portfolio assets
 */

import { apiService } from './api';
import { useSettingsStore } from '@/stores/settingsStore';

interface ExchangeRateCache {
  rate: number;
  timestamp: number;
}

class CurrencyConverter {
  private rateCache = new Map<string, ExchangeRateCache>();
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Get exchange rate from cache or API
   * @param from Source currency code
   * @param to Target currency code
   * @returns Exchange rate (how many 'to' units per 1 'from' unit)
   */
  async getExchangeRate(from: string, to: string): Promise<number> {
    // Normalize currency codes
    const fromUpper = from.toUpperCase();
    const toUpper = to.toUpperCase();

    // If same currency, return 1
    if (fromUpper === toUpper) {
      return 1;
    }

    // Check cache first
    const cacheKey = `${fromUpper}_${toUpper}`;
    const cached = this.rateCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.rate;
    }

    // Try to get from user settings first (as fallback)
    const settings = useSettingsStore.getState();
    const manualRate = this.getManualRate(fromUpper, toUpper, settings);
    if (manualRate !== null) {
      // Cache the manual rate
      this.rateCache.set(cacheKey, {
        rate: manualRate,
        timestamp: Date.now(),
      });
      return manualRate;
    }

    // Try to fetch from API
    try {
      const forexRate = await apiService.getForexRate(fromUpper, toUpper);
      const rate = forexRate.current_price;

      // Cache the rate
      this.rateCache.set(cacheKey, {
        rate,
        timestamp: Date.now(),
      });

      return rate;
    } catch (error) {
      console.warn(`Failed to fetch exchange rate ${fromUpper}/${toUpper}:`, error);

      // If API fails and we have a manual rate, use it
      if (manualRate !== null) {
        return manualRate;
      }

      // If no manual rate and API fails, try reverse rate
      try {
        const reverseRate = await apiService.getForexRate(toUpper, fromUpper);
        const rate = 1 / reverseRate.current_price;

        // Cache the rate
        this.rateCache.set(cacheKey, {
          rate,
          timestamp: Date.now(),
        });

        return rate;
      } catch (reverseError) {
        console.warn(`Failed to fetch reverse exchange rate ${toUpper}/${fromUpper}:`, reverseError);

        // Last resort: use default rates if available
        const defaultRate = this.getDefaultRate(fromUpper, toUpper);
        if (defaultRate !== null) {
          console.warn(`Using default exchange rate ${fromUpper}/${toUpper}: ${defaultRate}`);
          return defaultRate;
        }

        // If all else fails, throw error
        throw new Error(`Unable to get exchange rate ${fromUpper}/${toUpper}`);
      }
    }
  }

  /**
   * Convert a value from one currency to another
   * @param value Value to convert
   * @param from Source currency code
   * @param to Target currency code
   * @returns Converted value
   */
  async convert(value: number, from: string, to: string): Promise<number> {
    if (value === 0) return 0;

    const rate = await this.getExchangeRate(from, to);
    return value * rate;
  }

  /**
   * Get cached exchange rate (synchronous)
   * @param from Source currency code
   * @param to Target currency code
   * @returns Cached rate or null if not available
   */
  getCachedRate(from: string, to: string): number | null {
    const fromUpper = from.toUpperCase();
    const toUpper = to.toUpperCase();

    if (fromUpper === toUpper) {
      return 1;
    }

    const cacheKey = `${fromUpper}_${toUpper}`;
    const cached = this.rateCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.rate;
    }

    // Check manual rates from settings
    const settings = useSettingsStore.getState();
    const manualRate = this.getManualRate(fromUpper, toUpper, settings);
    return manualRate;
  }

  /**
   * Get manual exchange rate from settings
   */
  private getManualRate(
    from: string,
    to: string,
    settings: ReturnType<typeof useSettingsStore.getState>
  ): number | null {
    // Check for direct rate
    if (from === 'USD' && to === 'HKD' && settings.exchangeRates?.USD_HKD) {
      return settings.exchangeRates.USD_HKD;
    }
    if (from === 'USD' && to === 'CNY' && settings.exchangeRates?.USD_CNY) {
      return settings.exchangeRates.USD_CNY;
    }

    // Check for reverse rate
    if (from === 'HKD' && to === 'USD' && settings.exchangeRates?.USD_HKD) {
      return 1 / settings.exchangeRates.USD_HKD;
    }
    if (from === 'CNY' && to === 'USD' && settings.exchangeRates?.USD_CNY) {
      return 1 / settings.exchangeRates.USD_CNY;
    }

    return null;
  }

  /**
   * Get default exchange rate (last resort)
   */
  private getDefaultRate(from: string, to: string): number | null {
    // Approximate default rates (should be updated periodically)
    // These are rough estimates and should not be relied upon
    const defaults: Record<string, number> = {
      USD_HKD: 7.8, // Approximate USD to HKD
      USD_CNY: 7.2, // Approximate USD to CNY
    };

    const key = `${from}_${to}`;
    if (defaults[key]) {
      return defaults[key];
    }

    // Try reverse
    const reverseKey = `${to}_${from}`;
    if (defaults[reverseKey]) {
      return 1 / defaults[reverseKey];
    }

    return null;
  }

  /**
   * Clear the rate cache
   */
  clearCache(): void {
    this.rateCache.clear();
  }

  /**
   * Prefetch exchange rates for common currency pairs
   */
  async prefetchCommonRates(): Promise<void> {
    const commonPairs = [
      { from: 'USD', to: 'HKD' },
      { from: 'USD', to: 'CNY' },
      { from: 'HKD', to: 'USD' },
      { from: 'CNY', to: 'USD' },
    ];

    await Promise.allSettled(
      commonPairs.map((pair) => this.getExchangeRate(pair.from, pair.to))
    );
  }
}

export const currencyConverter = new CurrencyConverter();
